import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../utils/axiosInstance';
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import backendURL, { stripe_publishable_key } from "../../../config";

import {
  Typography, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Button as MuiButton, Box, CircularProgress, Alert, useTheme,
  ToggleButton, ToggleButtonGroup, Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
};

// --- Configuration ---
const API_BASE_URL = backendURL;
const STRIPE_PKEY = stripe_publishable_key;

if (!STRIPE_PKEY || STRIPE_PKEY === 'pk_test_YOUR_PUBLISHABLE_KEY') {
    console.warn("Stripe Publishable Key is not set or is using the default placeholder in PlanSelectionPage.js.");
}
const stripePromise = STRIPE_PKEY ? loadStripe(STRIPE_PKEY) : Promise.resolve(null);

const formatCurrency = (amount, currencyCode = 'usd') => {
    if (typeof amount !== 'number') return '';
    try {
         return new Intl.NumberFormat('en-US', {
             style: 'currency',
             currency: currencyCode?.toUpperCase() || 'USD',
         }).format(amount);
    } catch(e) {
         console.warn("Currency formatting failed:", e);
         return `$${amount.toFixed(2)}`;
    }
};

// --- Plan Card Component ---
// `billingCycle` ('month' | 'year') chooses which price label this card shows.
// Yearly subscribers are billed plan.price * 12 * 0.9 (10% off), computed
// client-side instead of via separate yearly DB rows.
const YEARLY_DISCOUNT_PCT = 10;
const pricingForCycle = (plan, billingCycle) => {
    const monthlyPrice = Number(plan?.price) || 0;
    if (monthlyPrice <= 0 || !billingCycle) {
        return { list: monthlyPrice, effective: monthlyPrice, monthlyEquivalent: null, hasDiscount: false, cycleLabel: 'month' };
    }
    if (billingCycle === 'year') {
        const list = monthlyPrice * 12;
        const effective = Math.round(list * (1 - YEARLY_DISCOUNT_PCT / 100) * 100) / 100;
        return { list, effective, monthlyEquivalent: effective / 12, hasDiscount: true, cycleLabel: 'year' };
    }
    return { list: monthlyPrice, effective: monthlyPrice, monthlyEquivalent: null, hasDiscount: false, cycleLabel: 'month' };
};

const PlanCard = ({ plan, billingCycle, onSelectPlan, isCurrentPlan, isLoading, theme }) => {
    const { id, display_name, price, currency, trial_days, features } = plan || {};
    if (!plan) return null;
    const handleSelect = () => { if (onSelectPlan && !isCurrentPlan && !isLoading) { onSelectPlan(plan); } };
    const p = pricingForCycle(plan, billingCycle);
    const hasDiscount = p.hasDiscount;
    const monthlyEquivalent = p.monthlyEquivalent;
    const featureList = [
        features.web_pages_limit === -1 ? 'Unlimited Web Pages' : features.web_pages_limit && `${features.web_pages_limit} Web Pages`,
        features.document_limit === -1 ? 'Unlimited Documents' : features.document_limit && `${features.document_limit} Documents`,
        features.llm_tokens_in_limit === -1 ? 'Unlimited LLM Tokens' : features.llm_tokens_in_limit && `${(features.llm_tokens_in_limit / 1000000).toFixed(1)}M In Tokens`,
        features.llm_tokens_out_limit === -1 ? '' : features.llm_tokens_out_limit && `${(features.llm_tokens_out_limit / 1000000).toFixed(1)}M Out Tokens`,
        features.multilingual && 'Multilingual Support', features.google_calendar && 'Google Calendar',
        features.ecommerce && 'E-commerce Features', features.ai_agents && 'AI Agents Access'
    ].filter(Boolean);

    return (
        <Box sx={{
            borderRadius: '16px', p: 3, minWidth: '260px', maxWidth: '320px', flex: '1 1 280px',
            display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
            bgcolor: isCurrentPlan ? 'rgba(5,150,105,0.04)' : 'rgba(255,255,255,0.75)',
            backdropFilter: 'blur(12px)',
            border: isCurrentPlan ? '2px solid rgba(5,150,105,0.3)' : '1px solid rgba(0,0,0,0.06)',
            boxShadow: isCurrentPlan ? '0 8px 32px rgba(5,150,105,0.1)' : '0 4px 24px rgba(0,0,0,0.04)',
            transition: 'all 0.25s ease',
            '&:hover': !isCurrentPlan ? {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                borderColor: `${theme.palette.primary.main}40`,
            } : {},
        }}>
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '8px',
                        background: isCurrentPlan
                            ? 'linear-gradient(135deg, #059669, #047857)'
                            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <StarOutlineIcon sx={{ color: '#fff', fontSize: '1rem' }} />
                    </Box>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.1rem', color: colors.textPrimary }}>
                        {display_name}
                    </Typography>
                </Box>

                {price > 0 && hasDiscount ? (
                    <Box sx={{ mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography sx={{
                                fontFamily, fontWeight: 700, fontSize: '1.75rem', color: theme.palette.primary.main,
                            }}>
                                {formatCurrency(p.effective, currency)}
                                <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.85rem', color: colors.textTertiary }}>
                                    {' '}/ {p.cycleLabel}
                                </Typography>
                            </Typography>
                            <Typography sx={{
                                fontFamily, fontSize: '0.85rem', color: colors.textTertiary,
                                textDecoration: 'line-through',
                            }}>
                                {formatCurrency(p.list, currency)}
                            </Typography>
                        </Box>
                        {monthlyEquivalent && (
                            <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textSecondary }}>
                                Equivalent to {formatCurrency(monthlyEquivalent, currency)} / month
                            </Typography>
                        )}
                        <Chip
                            size="small" color="success"
                            label={`Save ${YEARLY_DISCOUNT_PCT}%`}
                            sx={{ fontFamily, mt: 0.5, height: 20, fontWeight: 600 }}
                        />
                    </Box>
                ) : (
                    <Typography sx={{
                        fontFamily, fontWeight: 700, fontSize: '1.75rem', color: theme.palette.primary.main, mb: 0.5,
                    }}>
                        {price > 0 ? formatCurrency(p.effective, currency) : 'Free'}
                        {price > 0 && (
                            <Typography component="span" sx={{ fontFamily, fontWeight: 400, fontSize: '0.85rem', color: colors.textTertiary }}>
                                {' '}/ {p.cycleLabel}
                            </Typography>
                        )}
                    </Typography>
                )}

                {trial_days > 0 && (
                    <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary, fontStyle: 'italic', mb: 1.5 }}>
                        {trial_days}-day free trial available
                    </Typography>
                )}

                <Box sx={{ mt: 1.5 }}>
                    {featureList.map((feature, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem', color: '#059669' }} />
                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textSecondary }}>
                                {feature}
                            </Typography>
                        </Box>
                    ))}
                    {featureList.length === 0 && (
                        <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary }}>Basic Features</Typography>
                    )}
                </Box>
            </Box>

            <Box sx={{ mt: 2.5 }}>
                <MuiButton
                    fullWidth onClick={handleSelect}
                    disabled={isCurrentPlan || isLoading}
                    sx={{
                        fontFamily, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem',
                        borderRadius: '14px', py: 1.25,
                        ...(isCurrentPlan ? {
                            bgcolor: 'rgba(5,150,105,0.08)', color: '#059669',
                            border: '1px solid rgba(5,150,105,0.2)',
                            '&:disabled': { color: '#059669', bgcolor: 'rgba(5,150,105,0.08)' },
                        } : {
                            color: '#fff',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                boxShadow: `0 6px 20px ${theme.palette.primary.main}50`,
                                transform: 'translateY(-1px)',
                            },
                            '&:disabled': { opacity: 0.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: '#fff' },
                        }),
                        transition: 'all 0.2s ease',
                    }}
                >
                    {isLoading ? <CircularProgress size={20} color="inherit" />
                        : isCurrentPlan ? 'Current Plan' : 'Select Plan'}
                </MuiButton>
                {isCurrentPlan && (
                    <Typography sx={{ fontFamily, color: '#059669', mt: 1, textAlign: 'center', fontSize: '0.75rem', fontWeight: 600 }}>
                        Your active plan
                    </Typography>
                )}
            </Box>
        </Box>
    );
};


// --- Internal Stripe Checkout Form ---
const InternalCheckoutForm = ({ planToSubscribe, clientSecret, billingCycle, onSuccess, onCancel, onError }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();

    const callCreateSubscriptionWithCardAPI = async (planId, paymentMethodId) => {
        const body = { plan_id: planId, gateway_payment_method_id: paymentMethodId };
        // Yearly checkout adds the billing cycle so the subscription is stamped
        // with INTERVAL=YEAR (free plans always go monthly).
        if (planToSubscribe && Number(planToSubscribe.price) > 0 && billingCycle) {
            body.interval = billingCycle;
        }
        console.log("Calling POST /subscriptions with NEW card", body);
        return axiosInstance.post(`/subscriptions`, body);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!stripe || !elements) { setMessage("Stripe not loaded."); if (onError) onError("Stripe not loaded."); return; }
        setIsProcessing(true); setMessage(null);

        const { error: setupError, setupIntent } = await stripe.confirmSetup({
            elements, confirmParams: {}, redirect: 'if_required',
        });

        if (setupError) {
            const errorMsg = `Payment setup failed: ${setupError.message}`;
            setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
            return;
        }

        if (setupIntent?.status === 'succeeded') {
            const paymentMethodId = setupIntent.payment_method;
            if (!paymentMethodId) {
                const errorMsg = "Payment method details missing after setup.";
                setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
                return;
            }
            try {
                console.log(`SetupIntent success. PM ID: ${paymentMethodId}. Calling backend to subscribe...`);
                setMessage("Saving card and creating subscription...");
                const response = await callCreateSubscriptionWithCardAPI(planToSubscribe.id, paymentMethodId);

                if (response.data && response.status < 300) {
                    setMessage(`Subscribed to ${planToSubscribe.display_name}!`);
                    try {
                        const userStr = localStorage.getItem('user');
                        if (userStr) {
                            const prevUser = JSON.parse(userStr);
                            let updatedUser = {
                                ...prevUser,
                                active_team: {
                                ...prevUser.active_team,
                                subscription: response.data,
                                has_used_trial: (response.data && response.data?.plan?.price > 0) ? true : false
                                },
                                teams: prevUser.teams.map(team =>
                                team.team_id === prevUser.active_team.team_id
                                    ? {
                                        ...team,
                                        subscription: response.data,
                                        has_used_trial: (response.data && response.data?.plan?.price > 0) ? true : false}
                                    : team
                                ),
                            };
                            localStorage.setItem('user', JSON.stringify(updatedUser));
                        }
                    } catch (e) { console.error("Error updating user in local storage:", e); }

                     setTimeout(() => {
                        if (onSuccess) onSuccess(response.data);
                     }, 1500);

                } else {
                    throw new Error(response.data?.message || `Subscription failed with status ${response.status}`);
                }
            } catch (error) {
                console.error("Backend sub call failed after card setup:", error);
                const errorMsg = error.response?.data?.message || error.message || "Subscription creation failed after card setup.";
                setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
            }
        } else {
            const errorMsg = `Card setup status: ${setupIntent?.status}. Please try again or use a different card.`;
            setMessage(errorMsg); setIsProcessing(false); if (onError) onError(errorMsg);
        }
    };
    let userData = null;
    const userStr = localStorage.getItem('user');
    if (userStr) {
        userData = JSON.parse(userStr);
        if (userData && !userData.hasOwnProperty('has_used_trial')) {
            userData.has_used_trial = false
        }
        localStorage.setItem('user', JSON.stringify(userData));
    }
    const paymentElementOptions = { layout: "tabs" };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ p: 3, fontFamily }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: '10px',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <CreditCardOutlinedIcon sx={{ color: '#fff', fontSize: '1.1rem' }} />
                </Box>
                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: colors.textPrimary }}>
                    Add Payment Method for {planToSubscribe.display_name}
                </Typography>
            </Box>

            <PaymentElement id="payment-element" options={paymentElementOptions} />

            <Typography sx={{ mt: 2, fontFamily, fontSize: '0.8rem', color: colors.textSecondary }}>
                {planToSubscribe.price > 0 ?
                    (planToSubscribe.trial_days > 0 && userData && !userData.has_used_trial
                        ? `Your ${planToSubscribe.trial_days}-day trial starts now. Charge of ${formatCurrency(planToSubscribe.price, planToSubscribe.currency)} / ${planToSubscribe.interval} after trial.`
                        : `Charge of ${formatCurrency(planToSubscribe.price, planToSubscribe.currency)} / ${planToSubscribe.interval} starting today.`
                    )
                    : 'This plan is free. Adding card for potential future upgrades or charges.'}
                <br/> We'll securely save this card for future use.
            </Typography>

            {message && (
                 <Alert
                    severity={message.toLowerCase().includes('fail') || message.toLowerCase().includes('error') ? "error" : "info"}
                    sx={{ mt: 2, fontFamily, borderRadius: '14px' }}
                 >
                    {message}
                 </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <MuiButton
                    type="button" onClick={onCancel} disabled={isProcessing}
                    sx={{
                        fontFamily, textTransform: 'none', fontWeight: 500, borderRadius: '14px', px: 3,
                        color: colors.textSecondary, bgcolor: 'rgba(0,0,0,0.04)',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' },
                    }}
                >
                    Cancel
                </MuiButton>
                <MuiButton
                    type="submit"
                    disabled={isProcessing || !stripe || !elements}
                    sx={{
                        fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '14px', px: 3,
                        color: '#fff',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        boxShadow: `0 4px 16px ${theme.palette.primary.main}40`,
                        '&:hover': {
                            background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        },
                        '&:disabled': { opacity: 0.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: '#fff' },
                    }}
                    startIcon={isProcessing ? <CircularProgress size={18} color="inherit" /> : null}
                >
                    {isProcessing ? 'Processing...' : 'Save Card & Subscribe'}
                </MuiButton>
            </Box>
        </Box>
    );
};


// ========================================================================
// --- Main Plan Selection Page Component ---
// ========================================================================
const PricingPage = ({ onComplete, onError }) => {
    const [plans, setPlans] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentSubscription, setCurrentSubscription] = useState(null);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);

    const navigate = useNavigate();
    const theme = useTheme();

    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [initialError, setInitialError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [isActionLoading, setIsActionLoading] = useState(false);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [showBasicOptions, setShowBasicOptions] = useState(false);
    const [showStripeForm, setShowStripeForm] = useState(false);
    const [stripeClientSecret, setStripeClientSecret] = useState(null);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    // Billing-cycle toggle for signup. Defaults to monthly so first-time users
    // see the lowest entry price; they can flip to yearly to see the 10% saving.
    const [billingCycle, setBillingCycle] = useState('month');

    const handleLogout = () => {
        localStorage.clear();
        navigate("/log-out");
    };

    const ErrorMessage = ({ message }) => {
        if (!message) return null;
        return (
            <Box sx={{ display: "flex", alignItems: "center", my: 2, gap: 2 }}>
                <Alert severity="error" sx={{ flexGrow: 1, fontFamily, borderRadius: '14px' }}>
                    {typeof message === "object" ? JSON.stringify(message) : message}
                </Alert>
                <MuiButton
                    onClick={handleLogout}
                    sx={{
                        fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '14px', px: 3,
                        color: '#fff',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` },
                    }}
                >
                    Logout
                </MuiButton>
            </Box>
        );
    };

    const updateLocalStorageSubscription = (subscriptionData) => {
        try {
           const userStr = localStorage.getItem('user');
           if (userStr) {
               const prevUser = JSON.parse(userStr);
               let updatedUser = {
                    ...prevUser,
                    active_team: {
                    ...prevUser.active_team,
                    subscription: subscriptionData,
                    has_used_trial: (subscriptionData && subscriptionData?.plan?.price > 0) ? true : false
                    },
                    teams: prevUser.teams.map(team =>
                    team.team_id === prevUser.active_team.team_id
                        ? {
                            ...team,
                            subscription: subscriptionData,
                            has_used_trial: (subscriptionData && subscriptionData?.plan?.price > 0) ? true : false}
                        : team
                    ),
                };
               localStorage.setItem('user', JSON.stringify(updatedUser));
               setCurrentUser(updatedUser);
               console.log("Local storage user subscription updated.");
           } else {
                console.warn("User data not found in local storage for update.");
           }
        } catch (e) {
           console.error("Error updating user subscription in local storage:", e);
        }
   };

    const fetchInitialData = useCallback(async () => {
        setIsInitialLoading(true);
        setInitialError(null); setActionError(null);
        setSelectedPlan(null); setShowBasicOptions(false); setShowStripeForm(false);
        setStripeClientSecret(null); setShowConfirmationDialog(false);
        setPaymentMethods([]); setDefaultPaymentMethod(null);

        try {
            const userStr = localStorage.getItem('user');
            const userData = userStr ? JSON.parse(userStr) : null;
            const accountType = userData?.active_team.team_account_type || 'individual';
            const plansPromise = axiosInstance.get(`/plans?account_type=${accountType}`);
            const subPromise = axiosInstance.get(`/subscriptions/current`);
            const pmPromise = axiosInstance.get(`/payment-methods`);

            const [plansResult, subResult, pmResult] = await Promise.allSettled([
                plansPromise, subPromise, pmPromise
            ]);

            if (plansResult.status === 'fulfilled' && plansResult.value.data) {
                setPlans(plansResult.value.data);
            } else {
                console.error("Plan fetch failed:", plansResult.reason);
                setInitialError(plansResult.reason?.response?.data?.message || "Could not load available plans.");
            }

            if (subResult.status === 'fulfilled' && subResult.value.data) {
                updateLocalStorageSubscription(subResult.value.data);
                setCurrentSubscription(subResult.value.data);
                window.location.href = window.location.href = "/chatbots";
            } else if (subResult.reason?.response?.status === 404) {
                setCurrentSubscription(null);
            } else if (subResult.reason?.response?.status === 401) {
                 console.error("Subscription fetch failed (401):", subResult.reason);
                 setInitialError("Your session may have expired. Please log in again.");
                 if (onError) onError("Session expired.");
            } else {
                console.error("Subscription fetch failed:", subResult.reason);
                setInitialError(prev => prev ? `${prev} Also failed to load current subscription.` : "Failed to load current subscription details.");
            }

            if (pmResult.status === 'fulfilled' && pmResult.value.data) {
                console.log(pmResult)
                const methods = pmResult.value.data || [];
                setPaymentMethods(methods);
                const defaultMethod = methods.find(pm => pm.is_default);
                if (defaultMethod) {
                    setDefaultPaymentMethod(defaultMethod);
                    console.log("Default payment method found:", defaultMethod);
                } else {
                    console.log("No default payment method found.");
                }
            } else if (pmResult.reason?.response?.status === 401) {
                 console.error("Payment methods fetch failed (401):", pmResult.reason);
                 setInitialError(prev => prev ? `${prev} Session may have expired.` : "Your session may have expired. Please log in again.");
                 if (onError) onError("Session expired.");
            }
            else {
                console.warn("Payment methods fetch failed:", pmResult.reason);
            }

        } catch (err) {
            console.error("Error during initial data fetch setup:", err);
            setInitialError(err.message || "An unexpected error occurred while loading page data.");
            if (onError) onError(err.message);
        } finally {
            setIsInitialLoading(false);
        }
    }, [onError]);

    useEffect(() => {
        fetchInitialData();
    }, [fetchInitialData]);

    const isPlanPaid = (plan) => plan && plan.price > 0;

    const subscribeWithDefaultCard = useCallback(async () => {
        if (!selectedPlan || !defaultPaymentMethod || isActionLoading) return;

        setShowConfirmationDialog(false);
        setIsActionLoading(true); setActionError(null);
        console.log(`Subscribing to ${selectedPlan.name} using default PM: ${defaultPaymentMethod.id}`);
        try {
            const body = (defaultPaymentMethod) ?  { plan_id: selectedPlan.id , gateway_payment_method_id: defaultPaymentMethod.gateway_id }: { plan_id: selectedPlan.id };
            // Yearly subscribers carry the cycle through so the new subscription
            // is stamped INTERVAL=YEAR (free plans always go monthly).
            if (Number(selectedPlan.price) > 0 && billingCycle) {
                body.interval = billingCycle;
            }

            const response = await axiosInstance.post(`/subscriptions`, body);

            if (response.status < 300 && response.data) {
                console.log("Subscription successful using default card:", response.data);
                updateLocalStorageSubscription(response.data);
                setSelectedPlan(null);
                if (onComplete) onComplete(`Successfully subscribed to ${response.data.plan?.display_name || selectedPlan.display_name}!`);
            } else {
                throw new Error(response.data?.message || `Subscription failed with status ${response.status}`);
            }
        } catch (err) {
            console.error("Subscription with default card failed:", err);
            const errorMsg = err.response?.data?.message || "Subscription failed using the default payment method.";

            setShowConfirmationDialog(true);
            setShowStripeForm(false);
            setShowBasicOptions(false);
            setActionError(errorMsg);
        } finally {
            setIsActionLoading(false);
        }
    }, [selectedPlan, defaultPaymentMethod, isActionLoading, billingCycle, onComplete, onError]);

    const subscribeDirectly = useCallback(async (planToSubscribe) => {
        if (!planToSubscribe || isActionLoading) return;
        setIsActionLoading(true); setActionError(null);
        console.log(`Subscribing directly (no PM) to ${planToSubscribe.name}...`);
        try {
            // Direct subscribe is mostly freemium / trial paths -- but still pass
            // the cycle for paid plans so a yearly-on-trial conversion lands
            // correctly later.
            const body = { plan_id: planToSubscribe.id };
            if (Number(planToSubscribe.price) > 0 && billingCycle) {
                body.interval = billingCycle;
            }
            const response = await axiosInstance.post(`/subscriptions`, body);
            if (response.status < 300 && response.data) {
                 console.log("Direct subscription successful:", response.data);
                 updateLocalStorageSubscription(response.data);
                 setSelectedPlan(null);
                 setShowBasicOptions(false);
                 if (onComplete) onComplete(`Successfully subscribed to ${response.data.plan?.display_name || planToSubscribe.display_name}!`);
            } else {
                throw new Error(response.data?.message || `Subscription failed with status ${response.status}`);
            }
        } catch (err) {
            console.error("Direct subscription failed:", err);
            const errorMsg = err.response?.data?.message || "Subscription failed.";
            setActionError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setIsActionLoading(false);
        }
    }, [isActionLoading, billingCycle, onComplete, onError]);

    const initiateCardAddition = useCallback(async (planToSubscribe) => {
        if (!planToSubscribe || isActionLoading) return;

        setShowConfirmationDialog(false);
        setIsActionLoading(true); setActionError(null); setStripeClientSecret(null);
        console.log(`Initiating card addition for ${planToSubscribe.name}...`);
        try {
            const response = await axiosInstance.post(`/payment-methods/setup-intent`, {});
            if (response.data?.client_secret) {
                setStripeClientSecret(response.data.client_secret);
                setShowBasicOptions(false);
                setShowStripeForm(true);
                console.log("Setup Intent received.");
            } else {
                throw new Error("Client secret not received from setup intent.");
            }
        } catch (err) {
            console.error("Setup Intent fetch failed:", err);
            const errorMsg = err.response?.data?.message || "Could not initialize payment method setup.";
            setActionError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setIsActionLoading(false);
        }
    }, [isActionLoading, onError]);

    const handleSelectPlan = useCallback((plan) => {
        if (isActionLoading || plan.id === currentSubscription?.plan?.id) return;

        console.log("Selected Plan:", plan.name);
        setSelectedPlan(plan);
        setActionError(null);
        setShowBasicOptions(false);
        setShowStripeForm(false);
        setStripeClientSecret(null);
        setShowConfirmationDialog(false);

        const planNameLower = plan.name?.toLowerCase();
        if (!isPlanPaid(plan)) {
            subscribeDirectly(plan);
        } else if (planNameLower.includes('basic') && !defaultPaymentMethod) {
             setShowBasicOptions(true);
        } else {
            if (defaultPaymentMethod) {
                setShowConfirmationDialog(true);
            } else {
                initiateCardAddition(plan);
            }
        }
    }, [isActionLoading, currentSubscription, defaultPaymentMethod, subscribeDirectly, initiateCardAddition]);

    const handleBasicOptionClick = (addCard) => {
        if (!selectedPlan || isActionLoading) return;
        setShowBasicOptions(false);
        if (addCard) {
            initiateCardAddition(selectedPlan);
        } else {
            if(currentUser?.has_used_trial){
                setActionError("You have already used the trial. Now you need to add a card or use the freemium plan");
                setShowBasicOptions(true);
            }else{
                subscribeDirectly(selectedPlan);
            }
        }
    };

    const handleConfirmDialogClose = () => {
        if (isActionLoading) return;
        setShowConfirmationDialog(false);
        setSelectedPlan(null);
    };

    const handleConfirmDialogConfirm = () => {
        subscribeWithDefaultCard();
    };

    const handleConfirmDialogUseDifferentCard = () => {
        setShowConfirmationDialog(false);
        initiateCardAddition(selectedPlan);
    };

    const handleCheckoutSuccess = useCallback((backendSubscriptionData) => {
        console.log("Stripe Checkout + Backend Sub successful:", backendSubscriptionData);
        setSelectedPlan(null); setShowStripeForm(false); setStripeClientSecret(null); setActionError(null);
        if (onComplete) onComplete(`Successfully subscribed to ${backendSubscriptionData.plan?.display_name || 'the selected plan'}!`);
    }, [onComplete]);

    const handleCancelCheckout = useCallback(() => {
        console.log(selectedPlan,showStripeForm,showConfirmationDialog,showBasicOptions)
        console.log("Checkout cancelled.");
        setShowConfirmationDialog(false);
        setShowBasicOptions(false)
        setSelectedPlan(null); setShowStripeForm(false); setStripeClientSecret(null); setActionError(null); setIsActionLoading(false);setShowBasicOptions(false);
    }, []);

     const handleCheckoutError = useCallback((errorMessage) => {
         console.error("Error received from InternalCheckoutForm:", errorMessage);
         setIsActionLoading(false);
     }, []);

    // --- Render Logic ---
    if (isInitialLoading) {
        return (
            <Box sx={{ textAlign: 'center', py: 5 }}>
                <CircularProgress size={36} sx={{ color: theme.palette.primary.main }} />
                <Typography sx={{ mt: 2, fontFamily, color: colors.textSecondary, fontSize: '0.875rem' }}>
                    Loading plans...
                </Typography>
            </Box>
        );
    }

    if (initialError && !plans.length) {
        return <ErrorMessage message={`Failed to load page: ${initialError}`} />;
    }

    const currentPlanId = currentSubscription?.plan?.id;
    const stripeOptions = stripeClientSecret ? { clientSecret: stripeClientSecret, appearance: { theme: 'stripe' } } : null;

    return (
        <Box sx={{ fontFamily }}>
            {/* Heading */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography sx={{
                    fontFamily, fontWeight: 700, fontSize: '1.5rem', color: colors.textPrimary,
                    background: `linear-gradient(135deg, ${colors.textPrimary}, ${theme.palette.primary.main})`,
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                }}>
                    Choose Your Plan
                </Typography>
                <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, mt: 0.5 }}>
                    Select the plan that best fits your needs
                </Typography>
            </Box>

            {actionError && !showStripeForm && <ErrorMessage message={actionError} />}
            {initialError && plans.length > 0 && <ErrorMessage message={initialError} />}

            {/* Plan Cards */}
            {!selectedPlan && !showStripeForm && !showConfirmationDialog && !showBasicOptions && (() => {
                // The SAME plans render for both monthly and yearly cycles --
                // PlanCard computes the yearly display price (price * 12 * 0.9)
                // client-side. Toggle is shown whenever there's at least one
                // paid plan to apply it to.
                const anyPaidPlan = plans.some(p => Number(p.price) > 0);
                return (
                    <Box>
                        {anyPaidPlan && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                                <ToggleButtonGroup
                                    value={billingCycle}
                                    exclusive
                                    onChange={(_, val) => { if (val) setBillingCycle(val); }}
                                    size="small"
                                    color="primary"
                                    aria-label="billing cycle"
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.7)',
                                        backdropFilter: 'blur(8px)',
                                        borderRadius: '14px',
                                    }}
                                >
                                    <ToggleButton value="month" sx={{ fontFamily, textTransform: 'none', px: 3, fontWeight: 600 }}>
                                        Monthly
                                    </ToggleButton>
                                    <ToggleButton value="year" sx={{ fontFamily, textTransform: 'none', px: 3, fontWeight: 600 }}>
                                        Yearly
                                        <Chip
                                            size="small" color="success" label={`Save ${YEARLY_DISCOUNT_PCT}%`}
                                            sx={{ fontFamily, ml: 1, height: 20, fontWeight: 600 }}
                                        />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2.5 }}>
                            {plans.length > 0 ? (
                                plans.map(plan => (
                                    <PlanCard
                                        key={plan.id} plan={plan} billingCycle={billingCycle}
                                        onSelectPlan={handleSelectPlan}
                                        isCurrentPlan={plan.id === currentPlanId}
                                        isLoading={isActionLoading && selectedPlan?.id === plan.id}
                                        theme={theme}
                                    />
                                ))
                            ) : ( !isInitialLoading && !initialError && (
                                <Typography sx={{ fontFamily, textAlign: 'center', my: 3, color: colors.textTertiary }}>
                                    No plans available.
                                </Typography>
                            ))}
                        </Box>
                    </Box>
                );
            })()}

             {/* Basic Plan Options */}
             {showBasicOptions && selectedPlan?.name?.toLowerCase().includes('basic') && !isActionLoading && !defaultPaymentMethod && (
                 <Box sx={{
                     maxWidth: '480px', mx: 'auto', p: 3, textAlign: 'center',
                     borderRadius: '16px', bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)',
                     border: `1px solid ${theme.palette.primary.main}20`,
                     boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                 }}>
                     <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1.1rem', color: colors.textPrimary, mb: 1 }}>
                         Subscribe to {selectedPlan.display_name}
                     </Typography>
                     <Typography sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, mb: 3 }}>
                         Would you like to add a payment method now for potential future use?
                     </Typography>
                     <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                         <MuiButton
                             onClick={() => handleBasicOptionClick(true)}
                             sx={{
                                 fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '14px', px: 3, py: 1,
                                 color: '#fff',
                                 background: 'linear-gradient(135deg, #059669, #047857)',
                                 '&:hover': { background: 'linear-gradient(135deg, #047857, #059669)' },
                             }}
                         >
                             Yes, Add Card
                         </MuiButton>
                         <MuiButton
                             onClick={() => handleBasicOptionClick(false)}
                             sx={{
                                 fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '14px', px: 3, py: 1,
                                 color: '#fff',
                                 background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                 '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` },
                             }}
                         >
                             Start Basic Plan
                         </MuiButton>
                     </Box>
                     <MuiButton
                         onClick={handleCancelCheckout}
                         sx={{ mt: 2, fontFamily, textTransform: 'none', color: colors.textTertiary, fontSize: '0.8rem' }}
                     >
                        Cancel Selection
                     </MuiButton>
                 </Box>
             )}

             {/* Confirmation Dialog */}
             {selectedPlan && defaultPaymentMethod && (
                <Dialog
                    open={showConfirmationDialog}
                    onClose={handleConfirmDialogClose}
                    PaperProps={{
                        sx: { borderRadius: '16px', fontFamily, p: 1 }
                    }}
                >
                    <DialogTitle sx={{ fontFamily, fontWeight: 600 }}>Confirm Subscription</DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
                            Subscribe to the <strong>{selectedPlan.display_name}</strong> plan
                            ({formatCurrency(selectedPlan.price, selectedPlan.currency)} / {selectedPlan.interval})
                            using your saved card: <br />
                            <strong>{defaultPaymentMethod.brand?.toUpperCase()} ending in {defaultPaymentMethod.last4}</strong>?
                        </DialogContentText>
                        {actionError && <ErrorMessage message={actionError} />}
                    </DialogContent>
                    <DialogActions sx={{ p: 2, gap: 1 }}>
                        <MuiButton
                            onClick={handleConfirmDialogClose} disabled={isActionLoading}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '14px', color: colors.textSecondary }}
                        >
                            Cancel
                        </MuiButton>
                        <MuiButton
                            onClick={handleConfirmDialogUseDifferentCard} disabled={isActionLoading}
                            sx={{ fontFamily, textTransform: 'none', borderRadius: '14px', color: theme.palette.primary.main }}
                        >
                            Use Different Card
                        </MuiButton>
                        <MuiButton
                            onClick={handleConfirmDialogConfirm} disabled={isActionLoading}
                            sx={{
                                fontFamily, textTransform: 'none', fontWeight: 600, borderRadius: '14px', px: 3,
                                color: '#fff',
                                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                                '&:hover': { background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})` },
                                '&:disabled': { opacity: 0.5, background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`, color: '#fff' },
                            }}
                            startIcon={isActionLoading ? <CircularProgress size={18} color="inherit"/> : null}
                        >
                            {isActionLoading ? 'Processing...' : 'Confirm'}
                        </MuiButton>
                    </DialogActions>
                </Dialog>
             )}

            {/* Loading */}
            {isActionLoading && !showStripeForm && !showConfirmationDialog && !showBasicOptions && (
                 <Box sx={{ textAlign: 'center', my: 4 }}>
                     <CircularProgress size={30} sx={{ color: theme.palette.primary.main }} />
                     <Typography sx={{ mt: 1, fontFamily, fontSize: '0.85rem', color: colors.textSecondary }}>Processing...</Typography>
                 </Box>
            )}

            {/* Stripe Checkout Form */}
            {showStripeForm && selectedPlan && stripeOptions && stripePromise && (
                 <Box sx={{
                     maxWidth: '520px', mx: 'auto', mt: 3,
                     borderRadius: '16px', overflow: 'hidden',
                     bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)',
                     border: '1px solid rgba(0,0,0,0.06)',
                     boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
                 }}>
                    <Elements stripe={stripePromise} options={stripeOptions}>
                        <InternalCheckoutForm
                            planToSubscribe={selectedPlan}
                            clientSecret={stripeClientSecret}
                            billingCycle={billingCycle}
                            onSuccess={handleCheckoutSuccess}
                            onCancel={handleCancelCheckout}
                            onError={handleCheckoutError}
                        />
                    </Elements>
                 </Box>
             )}
        </Box>
    );
};

export default PricingPage;
